import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Download, Eye, Maximize2, X } from "lucide-react";
import PageWrapper from "@/pages/PageWrapper";
import { PROJECTS } from "@/data/projects";
import { MaskedLine, FadeUp, SectionLabel, LazyImage3D } from "@/components/Reveal";
import { useLightbox } from "@/components/Lightbox";
import useSEO from "@/lib/useSEO";
import CTABanner from "@/sections/CTABanner";

export default function ProjectDetailPage() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { open: openLightbox } = useLightbox();
    const [pdfOpen, setPdfOpen] = useState(false);
    const index = PROJECTS.findIndex((p) => p.slug === slug);
    const project = PROJECTS[index] || PROJECTS[0];
    const hasNext = PROJECTS.length > 1;
    const next = hasNext ? PROJECTS[(index + 1) % PROJECTS.length] : null;

    useSEO({
        title: `${project.title} \u2014 ${project.category}, ${project.location} | ate.teen studio`,
        description:
            `${project.title} by ate.teen studio in ${project.location}, Kerala. ` +
            `${project.area ? project.area + " \u00b7 " : ""}${project.category} project. ${project.story?.[0]?.slice(0, 160) || ""}`,
        path: `/portfolio/${project.slug}`,
    });

    const meta = [
        ["Type", project.category],
        ["Year", project.year],
        ["Location", `${project.location}, Kerala`],
    ];
    if (project.area) meta.push(["Area", project.area]);

    // ESC-to-close + scroll lock for PDF viewer
    useEffect(() => {
        if (!pdfOpen) return;
        const onKey = (e) => {
            if (e.key === "Escape") setPdfOpen(false);
        };
        document.addEventListener("keydown", onKey);
        window.__lenis?.stop();
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.removeEventListener("keydown", onKey);
            window.__lenis?.start();
            document.body.style.overflow = prev;
        };
    }, [pdfOpen]);

    return (
        <PageWrapper className="pt-28">
            <div className="mx-auto max-w-7xl px-6 sm:px-10">
                <button
                    data-testid="project-back-btn"
                    onClick={() => navigate("/portfolio")}
                    className="group flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.35em] text-ash transition-colors duration-300 hover:text-gold"
                >
                    <ArrowLeft
                        size={14}
                        className="transition-transform duration-300 group-hover:-translate-x-1"
                    />
                    All Projects
                </button>

                <div className="mt-10">
                    <SectionLabel index="04" title={project.category} />
                    <h1 className="mt-6 font-display text-4xl font-black uppercase leading-[0.95] tracking-tight sm:text-6xl lg:text-7xl">
                        <MaskedLine delay={0.05}>{project.title}</MaskedLine>
                    </h1>
                    <FadeUp delay={0.2} className="mt-5 flex flex-wrap gap-x-8 gap-y-2">
                        {meta.map(([k, v]) => (
                            <p
                                key={k}
                                className="text-[11px] font-medium uppercase tracking-[0.3em] text-ash"
                            >
                                <span className="text-gold">{k}</span> — {v}
                            </p>
                        ))}
                    </FadeUp>

                    {project.pdf && !project.pdfInline && (
                        <FadeUp delay={0.28} className="mt-8">
                            {project.pdfViewOnly ? (
                                <button
                                    type="button"
                                    data-testid="project-pdf-view"
                                    onClick={() => setPdfOpen(true)}
                                    className="group inline-flex items-center gap-3 rounded-full border border-gold bg-gold/10 px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.3em] text-gold shadow-[0_0_28px_-6px_rgba(200,169,106,0.55)] transition-all duration-300 hover:bg-gold hover:text-coal hover:shadow-[0_0_40px_-4px_rgba(200,169,106,0.85)]"
                                >
                                    <Eye
                                        size={14}
                                        className="transition-transform duration-300 group-hover:scale-110"
                                    />
                                    {project.pdfLabel || "View Plan"}
                                </button>
                            ) : (
                                <a
                                    data-testid="project-pdf-download"
                                    href={project.pdf}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    download
                                    className="group inline-flex items-center gap-3 rounded-full border border-gold bg-gold/10 px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.3em] text-gold shadow-[0_0_28px_-6px_rgba(200,169,106,0.55)] transition-all duration-300 hover:bg-gold hover:text-coal hover:shadow-[0_0_40px_-4px_rgba(200,169,106,0.85)]"
                                >
                                    <Download
                                        size={14}
                                        className="transition-transform duration-300 group-hover:translate-y-0.5"
                                    />
                                    {project.pdfLabel || "Download Plan (PDF)"}
                                </a>
                            )}
                        </FadeUp>
                    )}
                </div>
            </div>

            <FadeUp delay={0.3} className="mx-auto mt-12 max-w-[1500px] px-6 sm:px-10">
                <button
                    type="button"
                    data-testid="project-hero-image"
                    onClick={() => openLightbox(project.img, `${project.title} — hero`)}
                    aria-label={`Open ${project.title} hero image`}
                    className="group relative block h-[50vh] w-full overflow-hidden rounded-2xl border border-white/[0.08] sm:h-[75vh]"
                    data-cursor="hover"
                >
                    <LazyImage3D
                        src={project.img}
                        alt={`${project.title} by ate.teen studio`}
                        className="!absolute inset-0 h-full w-full"
                        imgClassName="transition-transform duration-[1.6s] ease-out group-hover:scale-[1.04]"
                        style={project.imgPosition ? { objectPosition: project.imgPosition } : undefined}
                        rotateX={12}
                        rotateY={-4}
                        scale={0.92}
                        y={40}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-coal/60 via-transparent to-transparent" />
                    <span className="absolute right-5 top-5 flex h-11 w-11 translate-y-2 items-center justify-center rounded-full border border-white/25 bg-coal/50 text-gold opacity-0 backdrop-blur-md transition-all duration-500 group-hover:translate-y-0 group-hover:opacity-100">
                        <Maximize2 size={16} />
                    </span>
                </button>
            </FadeUp>

            {/* Inline view-only PDF (Villa Plan, etc.) — auto-visible, no download */}
            {project.pdf && project.pdfInline && (
                <FadeUp
                    delay={0.35}
                    className="mx-auto mt-12 max-w-[1500px] px-6 sm:px-10"
                >
                    <div className="mb-4 flex items-end justify-between gap-4">
                        <div>
                            <p className="text-[10px] font-medium uppercase tracking-[0.35em] text-gold">
                                {project.pdfLabel || "Plan"}
                            </p>
                            <h3 className="mt-2 font-display text-2xl font-bold tracking-tight text-white sm:text-3xl">
                                Villa Plan
                            </h3>
                        </div>
                        <span className="hidden text-[10px] font-medium uppercase tracking-[0.3em] text-ash sm:block">
                            View-only · Download disabled
                        </span>
                    </div>
                    <div
                        data-testid="inline-pdf-viewer"
                        onContextMenu={(e) => e.preventDefault()}
                        className="relative h-[70vh] w-full overflow-hidden rounded-2xl border border-white/[0.08] bg-white shadow-[0_30px_80px_-10px_rgba(0,0,0,0.6)]"
                    >
                        <iframe
                            title={`${project.title} — ${project.pdfLabel || "Plan"}`}
                            src={`${project.pdf}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
                            className="h-full w-full"
                            sandbox="allow-scripts allow-same-origin"
                        />
                        {/* Invisible overlay strip along the top-right to mask the PDF viewer's built-in toolbar area (Chrome default renders one that may include a download button). This forces users to only view the content. */}
                        <div
                            aria-hidden="true"
                            onContextMenu={(e) => e.preventDefault()}
                            className="pointer-events-auto absolute right-0 top-0 z-10 h-12 w-52 bg-transparent"
                            style={{ cursor: "default" }}
                        />
                    </div>
                </FadeUp>
            )}

            <div className="mx-auto max-w-7xl px-6 py-24 sm:px-10">
                <div className="grid gap-16 lg:grid-cols-12">
                    <div className="lg:col-span-4">
                        <SectionLabel index="01" title="The Story" />
                    </div>
                    <div className="space-y-6 lg:col-span-8">
                        {project.story.map((para, i) => (
                            <FadeUp key={i} delay={i * 0.1}>
                                <p className="max-w-2xl text-base leading-relaxed text-ash sm:text-lg">
                                    {para}
                                </p>
                            </FadeUp>
                        ))}
                    </div>
                </div>

                <div className="mt-24">
                    <SectionLabel index="02" title="Gallery" />
                    <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-12">
                        {project.gallery.map((g, i) => (
                            <FadeUp key={i} delay={i * 0.05} className={g.span}>
                                <button
                                    type="button"
                                    data-testid={`gallery-shot-${i}`}
                                    onClick={() =>
                                        openLightbox(
                                            g.src || project.img,
                                            `${project.title} — ${g.caption}`,
                                        )
                                    }
                                    aria-label={`Open image — ${g.caption}`}
                                    className={`group relative block w-full overflow-hidden rounded-2xl border border-white/[0.07] ${g.h}`}
                                    data-cursor="hover"
                                >
                                    <LazyImage3D
                                        src={g.src || project.img}
                                        alt={`${project.title} — ${g.caption}`}
                                        className="!absolute inset-0 h-full w-full"
                                        imgClassName="transition-transform duration-[1.4s] ease-out group-hover:scale-105"
                                        style={{ objectPosition: g.position }}
                                        rotateX={14 + (i % 2 === 0 ? 0 : -4)}
                                        rotateY={i % 2 === 0 ? -6 : 6}
                                        scale={0.9}
                                        y={70}
                                        delay={i * 0.08}
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-coal/80 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                                    <span className="absolute right-4 top-4 flex h-10 w-10 translate-y-2 items-center justify-center rounded-full border border-white/25 bg-coal/50 text-gold opacity-0 backdrop-blur-md transition-all duration-500 group-hover:translate-y-0 group-hover:opacity-100">
                                        <Maximize2 size={14} />
                                    </span>
                                    <figcaption className="absolute bottom-5 left-5 translate-y-2 text-left opacity-0 transition-all duration-500 group-hover:translate-y-0 group-hover:opacity-100">
                                        <span className="text-[10px] font-medium uppercase tracking-[0.3em] text-gold">
                                            {String(i + 1).padStart(2, "0")} —{" "}
                                            {g.caption}
                                        </span>
                                    </figcaption>
                                </button>
                            </FadeUp>
                        ))}
                    </div>
                </div>

                {hasNext && next && (
                    <FadeUp className="mt-24">
                        <div
                            data-testid="next-project-card"
                            onClick={() => navigate(`/portfolio/${next.slug}`)}
                            className="group relative flex cursor-pointer items-center justify-between overflow-hidden rounded-2xl border border-white/[0.07] bg-graphite p-8 transition-colors duration-500 hover:border-gold/30 sm:p-12"
                            role="link"
                            aria-label={`Next project — ${next.title}`}
                        >
                            <div>
                                <p className="text-[10px] font-medium uppercase tracking-[0.35em] text-ash">
                                    Next Project
                                </p>
                                <h3 className="mt-2 font-display text-2xl font-bold text-white sm:text-4xl">
                                    {next.title}
                                </h3>
                            </div>
                            <span className="flex h-14 w-14 items-center justify-center rounded-full border border-gold/40 text-gold transition-all duration-500 group-hover:bg-gold group-hover:text-coal">
                                <ArrowRight size={20} />
                            </span>
                        </div>
                    </FadeUp>
                )}
            </div>

            <CTABanner />

            {/* View-only PDF modal — no download toolbar, blocked right-click */}
            <AnimatePresence>
                {pdfOpen && project.pdf && (
                    <motion.div
                        data-testid="pdf-viewer-overlay"
                        role="dialog"
                        aria-modal="true"
                        aria-label={`${project.title} — ${project.pdfLabel || "Plan"}`}
                        className="fixed inset-0 z-[130] flex items-center justify-center bg-black/95 p-3 backdrop-blur-sm sm:p-6"
                        onClick={() => setPdfOpen(false)}
                        onContextMenu={(e) => e.preventDefault()}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.25 }}
                    >
                        <button
                            data-testid="pdf-viewer-close"
                            onClick={() => setPdfOpen(false)}
                            aria-label="Close plan viewer"
                            className="absolute right-4 top-4 z-10 flex h-12 w-12 items-center justify-center rounded-full border border-white/20 bg-black/50 text-white backdrop-blur-md transition-colors duration-200 hover:border-gold hover:text-gold sm:right-8 sm:top-8"
                        >
                            <X size={20} />
                        </button>
                        <motion.div
                            onClick={(e) => e.stopPropagation()}
                            onContextMenu={(e) => e.preventDefault()}
                            initial={{ scale: 0.94, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.94, opacity: 0 }}
                            transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
                            className="relative h-[90vh] w-full max-w-6xl overflow-hidden rounded-xl border border-white/10 bg-white shadow-[0_30px_80px_-10px_rgba(0,0,0,0.8)]"
                        >
                            <iframe
                                title={`${project.title} — ${project.pdfLabel || "Plan"}`}
                                src={`${project.pdf}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
                                className="h-full w-full"
                                sandbox="allow-scripts allow-same-origin"
                            />
                            {/* Invisible overlay to block context menu on the iframe (extra layer) */}
                            <div
                                aria-hidden="true"
                                onContextMenu={(e) => e.preventDefault()}
                                className="pointer-events-none absolute inset-0"
                            />
                        </motion.div>
                        <p className="pointer-events-none absolute bottom-3 left-1/2 -translate-x-1/2 px-4 text-center text-[10px] font-medium uppercase tracking-[0.3em] text-white/50 sm:bottom-6">
                            Press ESC or tap outside to close · View-only
                        </p>
                    </motion.div>
                )}
            </AnimatePresence>
        </PageWrapper>
    );
}
